import { createSlice } from "@reduxjs/toolkit";

import { get, patch, post } from "../../helpers/axios";
import {
  ARCHIVE_SURVEY,
  GET_AUDIT_TRAIL,
  GET_DOWNLOAD_CSV,
  GET_DOWNLOAD_PDF,
  GET_MIL_DATA,
  GET_PRACTICES_DATA,
  GET_QUESTION_BY_ID,
  POST_APPROVE_SURVEY,
  POST_REJECT_SURVEY,
  POST_SUBMIT_QUESTION_BY_ID,
} from "../../helpers/url_helpers";
import { downloadBlob } from "../../helpers/utils";

const initialState = {
  questionData: [],
  surveyError: "",
  questionStatus: {},
  goalInfo: {},
  isPdfDownloading: false,
  isSurveyLoading: false,
  isCsvDownloading: false,
  auditData: [],
  submittedAnswer: {
    id: null,
    sub_category: null,
  },
  selectedGoal: {},
  selectedQuestion: {},
  selectedDomain: {},
  auditLoading: false,
};

const CRRsurvey = createSlice({
  name: "CRRsurvey",
  initialState,
  reducers: {
    setPdfLoading: (state, action) => {
      state.isPdfDownloading = action.payload.isPdfDownloading;
    },
    setCSVLoading: (state, action) => {
      state.isCsvDownloading = action.payload.isCsvDownloading;
    },
    setSurveyLoading: (state, action) => {
      state.isSurveyLoading = action.payload;
    },
    setAnswer: (state, action) => {
      state.submittedAnswer = action.payload.submittedAnswer;
    },
    setQuestionData: (state, action) => {
      state.questionData = action.payload.questionData;
    },
    setGoalInfo: (state, action) => {
      state.goalInfo = action.payload.goalInfo;
    },
    setMilData: (state, action) => {
      state.milData = action.payload.milData;
    },
    setPracticesData: (state, action) => {
      state.practicesData = action.payload.practicesData;
    },
    setAuditData: (state, action) => {
      state.auditData = action.payload.auditData;
    },
    setError: (state, action) => {
      state.surveyError = action.payload.surveyError;
    },
    // Reducer to update previous_submission
    updatePreviousSubmission: (state, action) => {
      const { questionId, newSubmission } = action.payload;
      const getUpdatedSubmission = (newSubmission) => ({
        answer: newSubmission.answer_id.toString(),
        rating: newSubmission.rating,
        category: newSubmission.subcategory_id,
        updated_at: new Date().toISOString(),
      });
      state.questionData.questions = state.questionData.questions.map(
        (question) => {
          if (question.id === questionId) {
            const previousSubmissions = question.previous_submission || [];

            // Filter null entries and update existing submissions for the subcategory
            const updatedPreviousSubmission = previousSubmissions
              .filter(Boolean)
              .map((submission) => {
                if (
                  !submission?.category || // Handles submissions without category
                  submission.category === newSubmission.subcategory_id
                ) {
                  return {
                    ...submission,
                    ...getUpdatedSubmission(newSubmission),
                  };
                }
                return submission;
              });

            const subcategoryExists = updatedPreviousSubmission.some(
              (submission) =>
                submission?.category === newSubmission.subcategory_id
            );
            // Add a new submission if no match exists for the subcategory
            if (!subcategoryExists) {
              updatedPreviousSubmission.push(
                getUpdatedSubmission(newSubmission)
              );
            }

            return {
              ...question,
              previous_submission: updatedPreviousSubmission,
            };
          }
          return question;
        }
      );
    },
    setSelectedDomain: (state, action) => {
      state.selectedDomain = action.payload;
    },
    setSelectedGoal: (state, action) => {
      state.selectedGoal = action.payload;
    },
    setSelectedQuestion: (state, action) => {
      state.selectedQuestion = action.payload;
    },

    setAuditLoading: (state, action) => {
      state.auditLoading = action.payload;
    },
    clearCRRSurvey: (state) => {
      return {
        ...initialState,
        selectedDomain: state.selectedDomain, // preserve this
      };
    },
  },
});

export const {
  setPdfLoading,
  setQuestionData,
  setMilData,
  setPracticesData,
  setError,
  setGoalInfo,
  setSurveyLoading,
  setCSVLoading,
  setAuditData,
  setAnswer,
  updatePreviousSubmission,
  setSelectedDomain,
  setSelectedGoal,
  setSelectedQuestion,
  clearCRRSurvey,
  setAuditLoading,
} = CRRsurvey.actions;
export default CRRsurvey.reducer;

export const fetchQuestions =
  (goalId, surveyId = "") =>
  async (dispatch) => {
    let response;
    try {
      dispatch(setSurveyLoading(true));
      dispatch(setError({ surveyError: "" }));
      dispatch(
        setQuestionData({
          questionData: {},
        })
      );

      // api call
      response = await get(
        GET_QUESTION_BY_ID.replace("{goalId}", goalId).replace(
          "{surveyId}",
          surveyId
        )
      );

      if (response) {
        dispatch(setSurveyLoading(false));

        if (response.error) {
          dispatch(
            setError({
              surveyError: response?.error,
            })
          );
        } else {
          const questions = [...(response?.data?.questions || [])].sort(
            (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
          );
          dispatch(
            setQuestionData({
              questionData: { ...response?.data, questions },
            })
          );
          if (questions.length > 0) {
            dispatch(setSelectedQuestion(questions[0]));
          }
        }
      }
    } catch (error) {
      dispatch(setSurveyLoading(false));
      if (error)
        dispatch(
          setError({
            surveyError: error,
          })
        );
    }
  };

export const submitAnswer = (payload, data, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setError({ surveyError: "" }));
    dispatch(
      setAnswer({
        submittedAnswer: { id: payload, sub_category: data?.subcategory_id },
      })
    );
    // api call
    response = await post(
      POST_SUBMIT_QUESTION_BY_ID.replace("{id}", payload),
      data
    );
    if (response) {
      dispatch(setAnswer({ submittedAnswer: {} }));
      if (response.error) {
        dispatch(
          setError({
            surveyError: response?.error,
          })
        );
      } else {
        dispatch(
          updatePreviousSubmission({
            questionId: payload, // The id of the question to update
            newSubmission: data, // updated info
          })
        );
        if (callback) callback();

        dispatch(
          setGoalInfo({
            goalInfo: response.data,
          })
        );
      }
    }
  } catch (error) {
    dispatch(setAnswer({ submittedAnswer: {} }));
    if (error)
      dispatch(
        setError({
          surveyError: error,
        })
      );
  }
};

export const getMilData =
  (domainId, surveyId = "") =>
  async (dispatch) => {
    let response;
    try {
      // dispatch(setQuestionData({ questionData: "" }));
      dispatch(setError({ surveyError: "" }));

      // api call
      response = await get(
        GET_MIL_DATA.replace("{domainId}", domainId).replace(
          "{surveyId}",
          surveyId
        )
      );

      if (response && response.error) {
        dispatch(
          setError({
            surveyError: response.error,
          })
        );
      } else if (response) {
        dispatch(
          setMilData({
            milData: response.data,
          })
        );
      }
    } catch (error) {
      if (error)
        dispatch(
          setError({
            surveyError: error,
          })
        );
    }
  };

export const getPracticesData =
  (domainId, surveyId = "") =>
  async (dispatch) => {
    let response;
    try {
      // dispatch(setQuestionData({ questionData: "" }));
      dispatch(setError({ surveyError: "" }));

      // api call
      response = await get(
        GET_PRACTICES_DATA.replace("{domainId}", domainId).replace(
          "{surveyId}",
          surveyId
        )
      );
      if (response) {
        response.error
          ? dispatch(
              setError({
                surveyError: response?.error,
              })
            )
          : dispatch(
              setPracticesData({
                practicesData: response.data,
              })
            );
      }
    } catch (error) {
      if (error)
        dispatch(
          setError({
            surveyError: error,
          })
        );
    }
  };

export const getAuditTrail =
  (surveyId = "", qnId = "", categoryId = "") =>
  async (dispatch) => {
    let response;
    try {
      dispatch(setError({ surveyError: "" }));
      dispatch(setAuditLoading(true));

      // api call
      response = await get(
        `${GET_AUDIT_TRAIL.replace("{qnId}", qnId).replace(
          "{surveyId}",
          surveyId
        )}${categoryId ? `&category_id=${categoryId}` : ""}`
      );
      if (response) {
        dispatch(setAuditLoading(false));
        response.error
          ? dispatch(
              setError({
                surveyError: response?.error,
              })
            )
          : dispatch(
              setAuditData({
                auditData: response.data,
              })
            );
      }
    } catch (error) {
      dispatch(setAuditLoading(false));
      if (error)
        dispatch(
          setError({
            surveyError: error,
          })
        );
    }
  };

export const approveSurvey = (payload, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setError({ surveyError: "" }));
    // api call
    response = await patch(POST_APPROVE_SURVEY, payload);

    if (response) {
      if (response.error) {
        dispatch(
          setError({
            surveyError: response.error,
          })
        );
      } else {
        if (callback) {
          callback();
        }
      }
    }
  } catch (error) {
    if (error)
      dispatch(
        setError({
          surveyError: error,
        })
      );
  }
};

export const rejectSurvey = (payload, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setError({ surveyError: "" }));

    // api call
    response = await patch(POST_REJECT_SURVEY, payload);

    if (response) {
      if (response.error) {
        dispatch(
          setError({
            surveyError: response?.error,
          })
        );
      } else if (callback) callback();
    }
  } catch (error) {
    if (error)
      dispatch(
        setError({
          surveyError: error,
        })
      );
  }
};

export const archiveSurvey = (payload, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setError({ surveyError: "" }));

    // api call
    response = await patch(ARCHIVE_SURVEY, payload);

    if (response) {
      if (response.error) {
        dispatch(
          setError({
            surveyError: response?.error,
          })
        );
      } else if (callback) callback();
    }
  } catch (error) {
    if (error)
      dispatch(
        setError({
          surveyError: error,
        })
      );
  }
};

export const downloadPDFReport = (surveyId, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setError({ surveyError: "" }));
    dispatch(setPdfLoading({ isPdfDownloading: true }));
    // api call
    response = await get(GET_DOWNLOAD_PDF.replace("{surveyId}", surveyId));

    if (response) {
      if (response.error) {
        dispatch(
          setError({
            surveyError: response?.error,
          })
        );
      } else {
        const pdfWindow = window.open();
        pdfWindow.location.href = response?.data?.pdf;
        if (callback) callback();
      }
      dispatch(setPdfLoading({ isPdfDownloading: false }));
    }
  } catch (error) {
    if (error)
      dispatch(
        setError({
          surveyError: error,
        })
      );
    dispatch(setPdfLoading({ isPdfDownloading: false }));
  }
};

export const downloadCSVReport =
  (surveyId, surveyName, callback) => async (dispatch) => {
    let response;
    try {
      dispatch(setError({ surveyError: "" }));
      dispatch(setCSVLoading({ isCsvDownloading: true }));
      // api call
      response = await get(GET_DOWNLOAD_CSV + surveyId);

      if (response) {
        if (response.error) {
          dispatch(
            setError({
              surveyError: response?.error,
            })
          );
        } else {
          downloadBlob(response, `${surveyName}.csv`);

          if (callback) callback();
        }
        dispatch(setCSVLoading({ isCsvDownloading: false }));
      }
    } catch (error) {
      if (error)
        dispatch(
          setError({
            surveyError: error,
          })
        );
      dispatch(setCSVLoading({ isCsvDownloading: false }));
    }
  };
